import Base from '../base/base';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './before-after-slider.scss';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Configuration options for BeforeAfterSlider
 */
interface BeforeAfterSliderOptions {
  /** CSS selector for the slider wrapper element */
  selector: string;
  /** Initial handle position as percentage (0-100), default 50 */
  initialPosition?: number;
  /** Enable scroll-triggered animation, default true */
  animateOnScroll?: boolean;
  /** Amount to animate right on scroll trigger (percentage), default 20 */
  scrollAnimateOffset?: number;
  /** Viewport percentage from top to trigger animation on scroll-down (0-100), default 60 */
  scrollTriggerPoint?: number;
  /** Viewport percentage from top for bottom-edge trigger on scroll-up (0-100), default 40 */
  scrollTriggerPointOnScrollUp?: number;
  /** Duration of "out" animation in seconds, default 0.35 */
  scrollAnimateDuration?: number;
  /** Duration of "snap back" animation in seconds, default 0.25 */
  scrollAnimateSnapBackDuration?: number;
  /** Delay before animation on scroll-up in seconds, default 0 */
  scrollAnimateDelayOnScrollUp?: number;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * BeforeAfterSlider
 * 
 * Interactive image comparison slider that reveals before/after images
 * via a draggable handle. Uses clip-path for smooth reveal effect.
 * 
 * Expected DOM structure:
 * ```html
 * <div class="ba-slider">
 *   <div class="ba-slider__image ba-slider__image--before">
 *     <img src="before.jpg" alt="Before">
 *   </div>
 *   <div class="ba-slider__image ba-slider__image--after">
 *     <img src="after.jpg" alt="After">
 *   </div>
 *   <div class="ba-slider__handle">
 *     <div class="ba-slider__handle-line"></div>
 *     <div class="ba-slider__handle-button">
 *       <div class="ba-slider__arrow ba-slider__arrow--left"><!-- SVG --></div>
 *       <div class="ba-slider__arrow ba-slider__arrow--right"><!-- SVG --></div>
 *     </div>
 *   </div>
 * </div>
 * ```
 */
export default class BeforeAfterSlider extends Base {
  private config: {
    selector: string;
    initialPosition: number;
    animateOnScroll: boolean;
    scrollAnimateOffset: number;
    scrollTriggerPoint: number;
    scrollTriggerPointOnScrollUp: number;
    scrollAnimateDuration: number;
    scrollAnimateSnapBackDuration: number;
    scrollAnimateDelayOnScrollUp: number;
  };

  // DOM references
  private wrapper: HTMLElement | null = null;
  private beforeImage: HTMLElement | null = null;
  private afterImage: HTMLElement | null = null;
  private handles: HTMLElement[] = [];

  // State
  private positions: number[] = [];
  private isDragging: boolean = false;
  private activeHandleIndex: number = -1;
  private hasAnimatedIn: boolean = false;

  // ScrollTrigger instances (for cleanup)
  private visibilityTrigger: ScrollTrigger | null = null;
  private animationTriggerDown: ScrollTrigger | null = null;
  private animationTriggerUp: ScrollTrigger | null = null;

  // Bound event handlers (for cleanup)
  private boundOnPointerDown: (e: PointerEvent) => void;
  private boundOnPointerMove: (e: PointerEvent) => void;
  private boundOnPointerUp: (e: PointerEvent) => void;
  private boundOnPointerCancel: (e: PointerEvent) => void;

  /**
   * @param options - Configuration options
   * @param options.selector - CSS selector for the slider wrapper
   * @param options.initialPosition - Starting position (0-100), default 50
   * @param options.animateOnScroll - Enable scroll animation, default true
   * @param options.scrollAnimateOffset - Percentage to animate right, default 20
   * @param options.scrollTriggerPoint - Viewport % from top to trigger on scroll-down, default 60
   * @param options.scrollTriggerPointOnScrollUp - Viewport % from top (bottom-edge trigger) on scroll-up, default 40
   * @param options.scrollAnimateDuration - "Out" animation duration in seconds, default 0.35
   * @param options.scrollAnimateSnapBackDuration - "Snap back" duration in seconds, default 0.25
   * @param options.scrollAnimateDelayOnScrollUp - Delay on scroll-up in seconds, default 0
   * @param options.debug - Enable debug logging
   */
  constructor(options: BeforeAfterSliderOptions) {
    super(options.debug || false);

    this.config = {
      selector: options.selector,
      initialPosition: options.initialPosition ?? 50,
      animateOnScroll: options.animateOnScroll ?? true,
      scrollAnimateOffset: options.scrollAnimateOffset ?? 20,
      scrollTriggerPoint: options.scrollTriggerPoint ?? 30,
      scrollTriggerPointOnScrollUp: options.scrollTriggerPointOnScrollUp ?? 100,
      scrollAnimateDuration: options.scrollAnimateDuration ?? 0.35,
      scrollAnimateSnapBackDuration: options.scrollAnimateSnapBackDuration ?? 0.35,
      scrollAnimateDelayOnScrollUp: options.scrollAnimateDelayOnScrollUp ?? 1
    };

    // Bind event handlers
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundOnPointerUp = this.onPointerUp.bind(this);
    this.boundOnPointerCancel = this.onPointerCancel.bind(this);

    this.init();
  }

  /**
   * Initialize the slider component
   */
  init(): void {
    this.log('BeforeAfterSlider initializing...', this.config.selector);

    // Find wrapper element
    this.wrapper = document.querySelector(this.config.selector);

    if (!this.wrapper) {
      console.error(`BeforeAfterSlider: Element not found for selector "${this.config.selector}"`);
      return;
    }

    // Cache DOM elements
    this.cacheDOM();

    if (!this.beforeImage || !this.afterImage || this.handles.length === 0) {
      console.error('BeforeAfterSlider: Required child elements not found', {
        beforeImage: !!this.beforeImage,
        afterImage: !!this.afterImage,
        handles: this.handles.length
      });
      return;
    }

    // Initialize positions array (one per handle)
    this.positions = this.handles.map(() => this.config.initialPosition);

    // Set initial positions
    this.updateAllPositions();

    // Bind events
    this.bindEvents();

    // Set up scroll-triggered animation
    if (this.config.animateOnScroll) {
      this.setupScrollTrigger();
    }

    // Add initialized class
    this.wrapper.classList.add('ba-slider--initialized');

    this.log('BeforeAfterSlider initialized');
  }

  /**
   * Cache DOM element references
   */
  private cacheDOM(): void {
    if (!this.wrapper) return;

    this.beforeImage = this.wrapper.querySelector('.ba-slider__image--before');
    this.afterImage = this.wrapper.querySelector('.ba-slider__image--after');
    this.handles = Array.from(this.wrapper.querySelectorAll('.ba-slider__handle'));

    this.log('DOM cached', {
      beforeImage: !!this.beforeImage,
      afterImage: !!this.afterImage,
      handles: this.handles.length
    });
  }

  /**
   * Bind pointer events for drag interaction
   */
  private bindEvents(): void {
    // Attach pointerdown to each handle
    this.handles.forEach((handle, index) => {
      handle.addEventListener('pointerdown', (e) => {
        this.activeHandleIndex = index;
        this.boundOnPointerDown(e);
      });
    });

    // Attach move and up to document for tracking outside bounds
    document.addEventListener('pointermove', this.boundOnPointerMove);
    document.addEventListener('pointerup', this.boundOnPointerUp);
    document.addEventListener('pointercancel', this.boundOnPointerCancel);

    this.log('Events bound');
  }

  /**
   * Set up ScrollTrigger for entrance animation
   * Uses three triggers: visibility tracking, scroll-down animation, scroll-up animation
   */
  private setupScrollTrigger(): void {
    if (!this.wrapper) return;

    this.log('Setting up ScrollTrigger', {
      triggerPoint: this.config.scrollTriggerPoint,
      triggerPointOnScrollUp: this.config.scrollTriggerPointOnScrollUp,
      offset: this.config.scrollAnimateOffset,
      duration: this.config.scrollAnimateDuration,
      snapBackDuration: this.config.scrollAnimateSnapBackDuration,
      delayOnScrollUp: this.config.scrollAnimateDelayOnScrollUp
    });

    // Trigger 1: Track visibility - reset when component leaves viewport
    this.visibilityTrigger = ScrollTrigger.create({
      trigger: this.wrapper,
      start: 'top bottom', // Element enters viewport from bottom
      end: 'bottom top',   // Element exits viewport from top
      onLeave: () => {
        // Scrolled past (element left top of viewport)
        this.resetToInitialPosition();
      },
      onLeaveBack: () => {
        // Scrolled back (element left bottom of viewport)
        this.resetToInitialPosition();
      }
    });

    // Trigger 2: Scroll-down animation - fires when top edge crosses trigger point
    this.animationTriggerDown = ScrollTrigger.create({
      trigger: this.wrapper,
      start: `top ${this.config.scrollTriggerPoint}%`,
      onEnter: () => {
        // Scrolling down, crossed trigger point - animate immediately
        this.animateToOffset();
      }
    });

    // Trigger 3: Scroll-up animation - fires when bottom edge crosses trigger point
    this.animationTriggerUp = ScrollTrigger.create({
      trigger: this.wrapper,
      start: `bottom ${this.config.scrollTriggerPointOnScrollUp}%`,
      onEnterBack: () => {
        // Scrolling up, bottom edge crossed trigger point
        const delay = this.config.scrollAnimateDelayOnScrollUp;
        if (delay > 0) {
          setTimeout(() => {
            this.animateToOffset();
          }, delay * 1000);
        } else {
          this.animateToOffset();
        }
      }
    });
  }

  /**
   * Animate handle to offset position and snap back
   */
  private animateToOffset(): void {
    if (this.hasAnimatedIn) {
      this.log('Already animated in, skipping');
      return;
    }

    this.hasAnimatedIn = true;
    this.log('Animating to offset');

    const targetPosition = this.config.initialPosition + this.config.scrollAnimateOffset;
    const clampedTarget = Math.min(100, Math.max(0, targetPosition));

    // Animate each handle
    this.handles.forEach((handle, index) => {
      // Animate handle position
      gsap.to(handle, {
        left: `${clampedTarget}%`,
        duration: this.config.scrollAnimateDuration,
        ease: 'power2.out',
        onComplete: () => {
          // Snap back to initial position
          gsap.to(handle, {
            left: `${this.config.initialPosition}%`,
            duration: this.config.scrollAnimateSnapBackDuration,
            ease: 'power3.inOut'
          });
        }
      });

      // Animate clip-path on after image
      if (this.afterImage) {
        gsap.to(this.afterImage, {
          clipPath: `inset(0 0 0 ${clampedTarget}%)`,
          duration: this.config.scrollAnimateDuration,
          ease: 'power2.out',
          onComplete: () => {
            gsap.to(this.afterImage, {
              clipPath: `inset(0 0 0 ${this.config.initialPosition}%)`,
              duration: this.config.scrollAnimateSnapBackDuration,
              ease: 'power3.inOut'
            });
          }
        });
      }

      // Update internal position state after full animation sequence
      const totalDuration = this.config.scrollAnimateDuration + this.config.scrollAnimateSnapBackDuration;
      setTimeout(() => {
        this.positions[index] = this.config.initialPosition;
      }, totalDuration * 1000);
    });
  }

  /**
   * Reset handle to initial position (no animation)
   */
  private resetToInitialPosition(): void {
    this.log('Resetting to initial position', this.config.initialPosition);

    this.hasAnimatedIn = false;

    // Kill any running animations
    this.handles.forEach((handle) => {
      gsap.killTweensOf(handle);
    });
    if (this.afterImage) {
      gsap.killTweensOf(this.afterImage);
    }
    gsap.killTweensOf(this);

    // Reset positions array
    this.positions = this.handles.map(() => this.config.initialPosition);

    // Reset DOM immediately (no animation)
    this.updateAllPositions();
  }

  /**
   * Handle pointer down (start drag)
   */
  private onPointerDown(e: PointerEvent): void {
    e.preventDefault();
    
    this.isDragging = true;
    
    // Capture pointer for reliable tracking
    const handle = this.handles[this.activeHandleIndex];
    if (handle) {
      handle.setPointerCapture(e.pointerId);
      handle.classList.add('ba-slider__handle--active');
    }

    // Add dragging class to wrapper
    this.wrapper?.classList.add('ba-slider--dragging');

    this.log('Drag started', { handleIndex: this.activeHandleIndex });
  }

  /**
   * Handle pointer move (during drag)
   */
  private onPointerMove(e: PointerEvent): void {
    if (!this.isDragging || !this.wrapper) return;

    // Calculate position as percentage
    const rect = this.wrapper.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    // Clamp to valid range
    const clampedPosition = this.clampPosition(percentage, this.activeHandleIndex);

    // Update position
    this.positions[this.activeHandleIndex] = clampedPosition;
    this.updatePosition(this.activeHandleIndex);
  }

  /**
   * Handle pointer up (end drag)
   */
  private onPointerUp(e: PointerEvent): void {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Release pointer capture
    const handle = this.handles[this.activeHandleIndex];
    if (handle) {
      handle.releasePointerCapture(e.pointerId);
      handle.classList.remove('ba-slider__handle--active');
    }

    // Remove dragging class
    this.wrapper?.classList.remove('ba-slider--dragging');

    this.log('Drag ended', { 
      handleIndex: this.activeHandleIndex,
      position: this.positions[this.activeHandleIndex]
    });

    this.activeHandleIndex = -1;
  }

  /**
   * Handle pointer cancel (browser took over gesture, e.g., for scrolling)
   */
  private onPointerCancel(e: PointerEvent): void {
    if (!this.isDragging) return;

    this.log('Pointer cancelled (browser took over gesture)');

    this.isDragging = false;

    // Clean up handle state
    const handle = this.handles[this.activeHandleIndex];
    if (handle) {
      // Pointer capture is already released by browser on cancel
      handle.classList.remove('ba-slider__handle--active');
    }

    // Remove dragging class
    this.wrapper?.classList.remove('ba-slider--dragging');

    this.activeHandleIndex = -1;
  }

  /**
   * Clamp position to valid range, respecting multi-handle constraints
   * 
   * @param position - Raw position percentage
   * @param handleIndex - Index of the handle being moved
   * @returns Clamped position
   */
  private clampPosition(position: number, handleIndex: number): number {
    // Basic bounds
    let min = 0;
    let max = 100;

    // Multi-handle constraints (handles can't cross each other)
    if (this.handles.length > 1) {
      // Can't go below previous handle
      if (handleIndex > 0) {
        min = this.positions[handleIndex - 1] + 1;
      }
      // Can't go above next handle
      if (handleIndex < this.handles.length - 1) {
        max = this.positions[handleIndex + 1] - 1;
      }
    }

    return Math.max(min, Math.min(max, position));
  }

  /**
   * Update visual position for a single handle
   * 
   * @param handleIndex - Index of the handle to update
   */
  private updatePosition(handleIndex: number): void {
    const position = this.positions[handleIndex];
    const handle = this.handles[handleIndex];

    if (!handle || !this.afterImage) return;

    // Update handle position
    handle.style.left = `${position}%`;

    // Update clip-path on after image
    // For single handle: clip from left at position
    // For multi-handle: would need more complex logic
    this.afterImage.style.clipPath = `inset(0 0 0 ${position}%)`;

    this.log('Position updated', { handleIndex, position });
  }

  /**
   * Update all handle positions (used on init)
   */
  private updateAllPositions(): void {
    this.handles.forEach((_, index) => {
      this.updatePosition(index);
    });
  }

  /**
   * Programmatically set the slider position
   * 
   * @param position - Position as percentage (0-100)
   * @param handleIndex - Which handle to move (default 0)
   */
  public setPosition(position: number, handleIndex: number = 0): void {
    if (handleIndex < 0 || handleIndex >= this.handles.length) {
      console.error('BeforeAfterSlider: Invalid handle index', handleIndex);
      return;
    }

    const clampedPosition = this.clampPosition(position, handleIndex);
    this.positions[handleIndex] = clampedPosition;
    this.updatePosition(handleIndex);
  }

  /**
   * Get current position of a handle
   * 
   * @param handleIndex - Which handle (default 0)
   * @returns Position as percentage
   */
  public getPosition(handleIndex: number = 0): number {
    return this.positions[handleIndex] ?? 0;
  }

  /**
   * Cleanup event listeners and styles
   */
  public destroy(): void {
    // Kill ScrollTrigger instances
    if (this.visibilityTrigger) {
      this.visibilityTrigger.kill();
      this.visibilityTrigger = null;
    }
    if (this.animationTriggerDown) {
      this.animationTriggerDown.kill();
      this.animationTriggerDown = null;
    }
    if (this.animationTriggerUp) {
      this.animationTriggerUp.kill();
      this.animationTriggerUp = null;
    }

    // Kill any running GSAP animations
    this.handles.forEach((handle) => {
      gsap.killTweensOf(handle);
    });
    if (this.afterImage) {
      gsap.killTweensOf(this.afterImage);
    }
    gsap.killTweensOf(this);

    // Remove event listeners from handles
    this.handles.forEach((handle) => {
      handle.removeEventListener('pointerdown', this.boundOnPointerDown);
    });

    // Remove document listeners
    document.removeEventListener('pointermove', this.boundOnPointerMove);
    document.removeEventListener('pointerup', this.boundOnPointerUp);
    document.removeEventListener('pointercancel', this.boundOnPointerCancel);

    // Clear inline styles
    this.handles.forEach((handle) => {
      handle.style.left = '';
      handle.classList.remove('ba-slider__handle--active');
    });

    if (this.afterImage) {
      this.afterImage.style.clipPath = '';
    }

    // Remove state classes
    this.wrapper?.classList.remove('ba-slider--initialized', 'ba-slider--dragging');

    this.log('BeforeAfterSlider destroyed');
  }
}
